import { List, Record } from 'immutable';
import moment from 'moment';

import BaseStore from './BaseStore';
import { ActionTypes } from '../constants/AppConstants';
import { yesNoToBoolean } from '../utils/text';
import {
    ACCESSIBILITY_PROFILE_STATUSES,
    ACCESSIBILITY_PROFILE_STEPS,
    BOOK_PA_STEPS,
    BOOKING_PA_STATUSES,
    EDIT_PA_SECTION_INDEXES,
} from '../components/account/passengerAssist/constants';
import AccessibilityProfileForm, { defaultAccessibilityProfile } from '../records/passengerAssist/AccessibilityProfileForm';
import Stepper from '../records/Stepper';
import AssistedTravel from '../records/passengerAssist/AssistedTravel';


const State = new Record({
    accessibilityProfile: defaultAccessibilityProfile,
    isAccessibilityProfileLoading: false,
    accessibilityProfileForm: new AccessibilityProfileForm(),
    accessibilityProfileStepper: new Stepper({
        steps: new List(Object.values(ACCESSIBILITY_PROFILE_STEPS).map(({ title }) => title)),
        activeStep: 0
    }),
    accessibilityProfileStatus: null,
    previousJourneys: new List(),
    isPreviousJourneysLoading: false,
    bookingPAStepper: new Stepper({
        steps: new List(Object.values(BOOK_PA_STEPS).map(({ title }) => title)),
        activeStep: 0
    }),
    bookingPAEnterAccessibilityProfileStep: ACCESSIBILITY_PROFILE_STEPS.PERSONAL_DETAILS.index,
    bookingPAStatus: BOOKING_PA_STATUSES.BOOK_OUTWARD_JOURNEY,
    useAccessibilityProfile: true,
    isEditPAModeEnabled: false,
    editPASection: EDIT_PA_SECTION_INDEXES.PERSONAL_DETAILS,
    assistedBookings: new List(),
    isAssistedBookingsLoading: false,
    assistedBooking: {},
    widgetToggled: false,
    isAssistedBookingLoading: false,
    travelAssist: new AssistedTravel(),
    pendingTravelAssistRequests: new List(),
    showBackToSearchResultsButton: false,
});

class PassengerAssistStore extends BaseStore {
    deserialize (serialized) {
        if (serialized) {
            const {
                accessibilityProfile,
                isAccessibilityProfileLoading,
                accessibilityProfileForm,
                accessibilityProfileStatus,
                accessibilityProfileStepper,
                assistedBooking,
                assistedBookings,
                isEditPAModeEnabled,
                editPASection,
                bookingPAStatus,
                bookingPAStepper,
                isAssistedBookingLoading,
                isAssistedBookingsLoading,
                isPreviousJourneysLoading,
                pendingTravelAssistRequests,
                previousJourneys,
                travelAssist,
                widgetToggled,
            } = serialized;

            const accessibilityProfileRecord = {
                ...accessibilityProfile,
                reasonForAssistance: new List(accessibilityProfileForm.reasonForAssistance),
            };

            return new State({
                accessibilityProfile: accessibilityProfileRecord,
                isAccessibilityProfileLoading,
                accessibilityProfileForm: new AccessibilityProfileForm(accessibilityProfileRecord),
                accessibilityProfileStatus,
                accessibilityProfileStepper: new Stepper({
                    activeStep: accessibilityProfileStepper.activeStep,
                    steps: new List(accessibilityProfileStepper.steps)
                }),
                assistedBooking,
                assistedBookings,
                isEditPAModeEnabled,
                editPASection,
                bookingPAStatus,
                useAccessibilityProfile: true,
                bookingPAStepper: new Stepper({
                    activeStep: bookingPAStepper.activeStep,
                    steps: new List(bookingPAStepper.steps)
                }),
                bookingPAEnterAccessibilityProfileStep: ACCESSIBILITY_PROFILE_STEPS.PERSONAL_DETAILS.index,
                isAssistedBookingLoading,
                isAssistedBookingsLoading,
                isPreviousJourneysLoading,
                pendingTravelAssistRequests: new List(pendingTravelAssistRequests.map(x => new AssistedTravel(x))),
                previousJourneys: new List(previousJourneys.map(journey => ({
                    ...journey,
                    legs: new List(previousJourneys.legs),
                }))),
                travelAssist: new AssistedTravel({
                    departureDate: moment(travelAssist.departureDate),
                    outwardJourney: travelAssist.outwardJourney,
                    returnJourney: travelAssist.returnJourney,
                }),
                widgetToggled,
                showBackToSearchResultsButton: false,
            });
        }
        return new State();
    }

    getInitialState() {
        return new State();
    }

    addOrderTripIdToTravelAssist({ orderTripId }) {
        const lastItem = this.state.pendingTravelAssistRequests.last();

        if (lastItem && !lastItem.orderTripId) {
            const updatedLastItem = this.state.pendingTravelAssistRequests.last().set('orderTripId', orderTripId);

            return this.setState(
                this.state.set('pendingTravelAssistRequests',
                this.state.pendingTravelAssistRequests
                    .pop()
                    .push(updatedLastItem))
            );
        } else {
            return false;
        }
    }

    getPendingPassengerAssistRequests () {
        return this.state.get('pendingTravelAssistRequests');
    }

    setAccessibilityProfileForm({ accessibilityProfileFields }) {
        const updatedProfileForm = this.state.accessibilityProfileForm ? this.state.accessibilityProfileForm.merge(accessibilityProfileFields) : new AccessibilityProfileForm();
        return this.setState(this.state.set('accessibilityProfileForm', updatedProfileForm));
    }

    resetAccessibilityProfileForm({ accessibilityProfileFields }) {
        const accessibilityProfile = {...defaultAccessibilityProfile, ...accessibilityProfileFields};
        return this.setState(
            this.state.set('accessibilityProfileForm', new AccessibilityProfileForm(accessibilityProfile)),
        );
    }

    setLoadedAccessibilityProfile ({ accessibilityProfile }) {
        const normalizedAccessibilityProfile = this.normalizeGetAccessibilityProfileResponse(accessibilityProfile);
        this.setState(
            this.state
                .set('accessibilityProfile', normalizedAccessibilityProfile)
                .set('accessibilityProfileStatus', normalizedAccessibilityProfile.id ? ACCESSIBILITY_PROFILE_STATUSES.VIEW : ACCESSIBILITY_PROFILE_STATUSES.CREATE)
                .set('accessibilityProfileForm', this.state.accessibilityProfileForm.merge(normalizedAccessibilityProfile))
                .set('isAccessibilityProfileLoading', false)
        );
    }

    setIsAccessibilityProfileLoading(value) {
        return function() {
            this.setState(this.state.set('isAccessibilityProfileLoading', value));
        };
    }

    setAccessibilityProfile({ accessibilityProfile }) {
        this.setState(this.state.set('accessibilityProfile', accessibilityProfile));
    }

    normalizeGetAccessibilityProfileResponse(response) {
        if (response && response.id) {
            return {
                title: response.title,
                firstName: response.firstNames,
                surname: response.surname,
                phone: response.contactNumber,
                postCode: response.postCode,
                line1: response.addressLine1,
                line2: response.addressLine2,
                line3: response.addressLine3,
                town: response.town,
                country: response.county,
                contactMethod: response.contactMethod,
                assistanceLocation: response.assistanceLocation,
                otherAssistanceLocation: response.otherAssistanceLocation,
                reasonForAssistance: Array.isArray(response.reasonForAssistance)
                    ? new List(response.reasonForAssistance)
                    : response.reasonForAssistance,
                otherAssistanceType: response.otherAssistanceType,
                participateSurvey: yesNoToBoolean(response.participateSurvey),
                participateImprovementSurvey: null,
                pushNotify: response.pushNotify,
                id: response.id,
            };
        }
        return {};
    }

    setAccessibilityStatus({ status }) {
        this.setState(this.state.set('accessibilityProfileStatus', status));
    }

    createAccessibilityProfile({ accessibilityProfile }) {
        this.setState(this.state
            .set('accessibilityProfile', this.normalizeGetAccessibilityProfileResponse(accessibilityProfile))
            .set('accessibilityProfileStatus', ACCESSIBILITY_PROFILE_STATUSES.CREATED)
        );
    }

    updateAccessibilityProfile({ accessibilityProfile }) {
        this.setState(this.state
            .set('accessibilityProfile', this.normalizeGetAccessibilityProfileResponse(accessibilityProfile))
            .set('accessibilityProfileStatus', ACCESSIBILITY_PROFILE_STATUSES.VIEW)
        );
    }

    deleteAccessibilityProfile() {
        this.setState(this.state
            .set('accessibilityProfile', defaultAccessibilityProfile)
            .set('accessibilityProfileStatus', ACCESSIBILITY_PROFILE_STATUSES.CREATE)
        );
    }

    getAccessibilityProfile() {
        return this.state.get('accessibilityProfile');
    }

    getAccessibilityProfileId() {
        return this.state.get('accessibilityProfile').id;
    }

    setPreviousJourneys({ previousJourneys, departureDate }) {
        const normalizedPreviousJourneys = previousJourneys.map(previousJourney => {
            const previousJourneyDepartureDate = moment(previousJourney.departureDate, 'DD/MM/YYYY');
            const previousJourneyArrivalDate = moment(previousJourney.arrivalDate, 'DD/MM/YYYY');
            const journeyDaysDiff = previousJourneyArrivalDate.diff(previousJourneyDepartureDate, 'days');
            const legs = previousJourney.legs.map(leg => {
                const legDepartureDate = moment(leg.departureDate, 'DD/MM/YYYY');
                const legArrivalDate = moment(leg.arrivalDate, 'DD/MM/YYYY');
                const legsDaysDiff = legArrivalDate.diff(legDepartureDate, 'days');
                return {
                    ...leg,
                    departureDate: departureDate.format('DD/MM/YYYY'),
                    arrivalDate: departureDate.clone().add(legsDaysDiff, 'days').format('DD/MM/YYYY'),
                };
            });

            return {
                ...previousJourney,
                departureDate: departureDate.format('DD/MM/YYYY'),
                arrivalDate: departureDate.clone().add(journeyDaysDiff, 'days').format('DD/MM/YYYY'),
                legs,
            };
        });

        const filterPreviousJourneys = (journeys) => {
            const isDepartureDateToday = departureDate.isSame(moment(), 'day');
            if (!isDepartureDateToday) {
                return journeys;
            }
            return journeys.filter(journey => this.state.travelAssist.getJourneyDepartureMoment(journey).isAfter(moment()));
        };

        const prevJourneys = filterPreviousJourneys(normalizedPreviousJourneys);
        this.setState(
            this.state.set(
                'previousJourneys',
                new List(prevJourneys.map(prevJourney => ({ ...prevJourney, legs: new List(prevJourney.legs) }) ))
            )
        );
    }

    setIsPreviousJourneysLoading(value) {
        return function() {
            this.setState(this.state.set('isPreviousJourneysLoading', value));
        };
    }

    setBookingPAStatus({ status }) {
        this.setState(this.state.set('bookingPAStatus', status));
    }

    setIsEditPAModeEnabled ({ isEditPAModeEnabled }) {
        this.setState(this.state.set('isEditPAModeEnabled', isEditPAModeEnabled));
    }

    setEditPASection({ sectionIndex }) {
        this.setState(this.state.set('editPASection', sectionIndex));
    }

    setAssistedBookings({ assistedBookings }) {
        this.setState(this.state.set('assistedBookings', new List(assistedBookings)));
    }

    setAssistedBooking({ assistedBooking }) {
        this.setState(this.state.set('assistedBooking', assistedBooking));
    }

    setPassengerAssistWidgetToggled ({ toggled }) {
        this.setState(this.state
            .set('widgetToggled', toggled)
        );
    }

    handleClear () {
        this.setState(this.getInitialState());
    }

    setAssistedBookingsLoading(value) {
        return function() {
            this.setState(this.state.set('isAssistedBookingsLoading', value));
        };
    }

    setAssistedBookingLoading(value) {
        return function() {
            this.setState(this.state.set('isAssistedBookingLoading', value));
        };
    }

    setAccessibilityProfileStep ({ step }) {
        return this.setState(this.state.set('accessibilityProfileStepper', this.state.accessibilityProfileStepper.setActiveStep(step)));
    }

    setPrevAccessibilityProfileStep () {
        return this.setState(this.state.set('accessibilityProfileStepper', this.state.accessibilityProfileStepper.handlePrevStep()));
    }

    setNextAccessibilityProfileStep () {
        return this.setState(this.state.set('accessibilityProfileStepper', this.state.accessibilityProfileStepper.handleNextStep()));
    }

    setBookingPAStep ({ step }) {
        return this.setState(this.state.set('bookingPAStepper', this.state.bookingPAStepper.setActiveStep(step)));
    }

    setPrevBookingPAStep () {
        return this.setState(this.state.set('bookingPAStepper', this.state.bookingPAStepper.handlePrevStep()));
    }

    setNextBookingPAStep() {
        return this.setState(this.state.set('bookingPAStepper', this.state.bookingPAStepper.handleNextStep()));
    }

    setUseAccessibilityProfile({ useAccessibilityProfile }) {
        return this.setState(this.state.set('useAccessibilityProfile', useAccessibilityProfile));
    }

    deleteTravelAssist({ travelAssist }) {
        return this.setState(
            this.state.set(
                'pendingTravelAssistRequests',
                this.state.pendingTravelAssistRequests.filter(req => req.orderTripId !== travelAssist.orderTripId)
            )
        );
    }

    setTravelAssist({ travelAssist }) {
        const [key, value] = Object.entries(travelAssist)[0];
        return this.setState(
            this.state.set('travelAssist', this.state.travelAssist.set(key, value)),
        );
    }

    setPendingTravelAssistRequests({ travelAssistRequests }) {
        const updated = this.state.pendingTravelAssistRequests.concat(travelAssistRequests);

        return this.setState(
            this.state.set('pendingTravelAssistRequests', updated),
        );
    }

    setPendingTravelAssistRequestEnabled ({ params }) {
        const {
            orderTripId,
            enabled,
            direction,
        } = params;

        const newState = this.state.pendingTravelAssistRequests.map(req => {
            if (req.orderTripId === orderTripId) {
                if (direction === 'Outward') {
                    return req.setOutwardEnabled(enabled);
                } else {
                    return req.setReturnEnabled(enabled);
                }
            } else {
                return req;
            }
        });

        return this.setState(
            this.state.set('pendingTravelAssistRequests', newState),
        );
    }

    getPendingTravelAssistRequests() {
        return this.get('pendingTravelAssistRequests');
    }

    clearPendingTravelAssistRequests() {
        return this.setState(
            this.state.set('pendingTravelAssistRequests', new List()),
        );
    }

    resetTravelAssist() {
        return this.setState(this.state.set('travelAssist', new AssistedTravel()));
    }

    resetOutwardJourney() {
        return this.setState(this.state.set('travelAssist', this.state.travelAssist.set('outwardJourney', {})));
    }

    resetReturnJourney() {
        return this.setState(this.state.set('travelAssist', this.state.travelAssist.set('returnJourney', {})));
    }

    setBookingPAEnterAccessibilityProfileStep({ step }) {
        return this.setState(this.state.set('bookingPAEnterAccessibilityProfileStep', step));
    }

    setShowBackToSearchResultsButton({ showButton }) {
        return this.setState(this.state.set('showBackToSearchResultsButton', showButton));
    }

    getActionHandlers() {
        return {
            [ActionTypes.SET_ACCESSIBILITY_PROFILE_FORM]: this.setAccessibilityProfileForm,
            [ActionTypes.AUTO_POPULATE_ACCESSIBILITY_PROFILE_FORM]: this.setAccessibilityProfileForm,
            [ActionTypes.RESET_ACCESSIBILITY_PROFILE_FORM]: this.resetAccessibilityProfileForm,
            [ActionTypes.SET_ACCESSIBILITY_PROFILE_STATUS]: this.setAccessibilityStatus,
            [ActionTypes.GET_ACCESSIBILITY_PROFILE_STARTED]: this.setIsAccessibilityProfileLoading(true),
            [ActionTypes.GET_ACCESSIBILITY_PROFILE_LOADED]: this.setLoadedAccessibilityProfile,
            [ActionTypes.GET_ACCESSIBILITY_PROFILE_FINISHED]: this.setIsAccessibilityProfileLoading(false),
            [ActionTypes.SET_ACCESSIBILITY_PROFILE]: this.setAccessibilityProfile,
            [ActionTypes.CREATE_ACCESSIBILITY_PROFILE_LOADED]: this.createAccessibilityProfile,
            [ActionTypes.UPDATE_ACCESSIBILITY_PROFILE_LOADED]: this.updateAccessibilityProfile,
            [ActionTypes.DELETE_ACCESSIBILITY_PROFILE_SUCCESS]: this.deleteAccessibilityProfile,
            [ActionTypes.SET_BOOKING_PA_STATUS]: this.setBookingPAStatus,
            [ActionTypes.SET_BOOKING_PA_ENTER_ACCESSIBILITY_PROFILE_STEP]: this.setBookingPAEnterAccessibilityProfileStep,
            [ActionTypes.RESET_OUTWARD_JOURNEY]: this.resetOutwardJourney,
            [ActionTypes.RESET_RETURN_JOURNEY]: this.resetReturnJourney,
            [ActionTypes.USE_ACCESSIBILITY_PROFILE]: this.setUseAccessibilityProfile,
            [ActionTypes.SET_IS_EDIT_PA_MODE_ENABLED]: this.setIsEditPAModeEnabled,
            [ActionTypes.SET_EDIT_PA_SECTION]: this.setEditPASection,
            [ActionTypes.SET_SHOW_BACK_TO_SEARCH_RESULTS_BUTTON]: this.setShowBackToSearchResultsButton,
            [ActionTypes.GET_PREVIOUS_JOURNEYS_STARTED]: this.setIsPreviousJourneysLoading(true),
            [ActionTypes.GET_PREVIOUS_JOURNEYS_SUCCESS]: this.setPreviousJourneys,
            [ActionTypes.GET_PREVIOUS_JOURNEYS_FINISHED]: this.setIsPreviousJourneysLoading(false),
            [ActionTypes.GET_ASSISTED_BOOKINGS_STARTED]: this.setAssistedBookingsLoading(true),
            [ActionTypes.GET_ASSISTED_BOOKINGS_SUCCESS]: this.setAssistedBookings,
            [ActionTypes.GET_ASSISTED_BOOKINGS_FINISHED]: this.setAssistedBookingsLoading(false),
            [ActionTypes.GET_ASSISTED_BOOKING_STARTED]: this.setAssistedBookingLoading(true),
            [ActionTypes.GET_ASSISTED_BOOKING_SUCCESS]: this.setAssistedBooking,
            [ActionTypes.SET_PASSENGER_ASSIST_WIDGET_TOGGLED]: this.setPassengerAssistWidgetToggled,

            [ActionTypes.LOGGED_OUT]: this.handleClear,
            [ActionTypes.BASKET_CLEARED]: this.handleClear,
            [ActionTypes.ORDER_EXPIRED]: this.handleClear,

            [ActionTypes.GET_ASSISTED_BOOKING_FINISHED]: this.setAssistedBookingLoading(false),
            [ActionTypes.SET_ACCESSIBILITY_PROFILE_STEP]: this.setAccessibilityProfileStep,
            [ActionTypes.SET_NEXT_ACCESSIBILITY_PROFILE_STEP]: this.setNextAccessibilityProfileStep,
            [ActionTypes.SET_PREV_ACCESSIBILITY_PROFILE_STEP]: this.setPrevAccessibilityProfileStep,
            [ActionTypes.SET_BOOKING_PA_STEP]: this.setBookingPAStep,
            [ActionTypes.SET_NEXT_BOOKING_PA_STEP]: this.setNextBookingPAStep,
            [ActionTypes.SET_PREV_BOOKING_PA_STEP]: this.setPrevBookingPAStep,
            [ActionTypes.SET_TRAVEL_ASSIST]: this.setTravelAssist,
            [ActionTypes.DELETE_TRAVEL_ASSIST]: this.deleteTravelAssist,
            [ActionTypes.SET_PENDING_TRAVEL_ASSIST_REQUESTS]: this.setPendingTravelAssistRequests,
            [ActionTypes.SET_PENDING_TRAVEL_ASSIST_REQUEST_ENABLED]: this.setPendingTravelAssistRequestEnabled,
            [ActionTypes.CLEAR_PENDING_TRAVEL_ASSIST_REQUESTS]: this.clearPendingTravelAssistRequests,
            [ActionTypes.ADD_ORDER_TRIP_ID_TO_PASSENGER_ASSIST_REQUEST]: this.addOrderTripIdToTravelAssist,

            [ActionTypes.RESET_TRAVEL_ASSIST]: this.resetTravelAssist,
        };
    }
}

const instance = new PassengerAssistStore({ storageKey: 'passenger-assist' });

export default instance;
